import { useTranslation } from 'react-i18next';
import { FileWarning } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PrintFileBreakdown } from '../types';
import { EmptyState } from './EmptyState';

// Known reason labels (matches print file issue reason config)
const REASON_LABELS: Record<string, string> = {
  wrong_design: 'Wrong Design',
  missing_artwork: 'Missing Artwork',
  color_mismatch: 'Color Mismatch',
  low_resolution: 'Low Resolution',
  wrong_position: 'Wrong Position',
  other: 'Other Issue',
};

// Colors for known reasons
const KNOWN_REASON_COLORS: Record<string, string> = {
  wrong_design: 'hsl(25, 95%, 53%)',
  missing_artwork: 'hsl(0, 72%, 51%)',
  color_mismatch: 'hsl(280, 60%, 55%)',
  low_resolution: 'hsl(38, 92%, 50%)',
  wrong_position: 'hsl(221, 83%, 53%)',
  other: 'hsl(215, 14%, 60%)',
};

const CUSTOM_PALETTE = [
  'hsl(160, 60%, 45%)',
  'hsl(330, 65%, 50%)',
  'hsl(190, 70%, 45%)',
  'hsl(100, 50%, 45%)',
];

function getReasonColor(reason: string, customIndex: number): string {
  return KNOWN_REASON_COLORS[reason] ?? CUSTOM_PALETTE[customIndex % CUSTOM_PALETTE.length] ?? 'hsl(215, 14%, 60%)';
}

interface PrintFileBreakdownChartProps {
  data: PrintFileBreakdown[];
  isLoading?: boolean;
}

export function PrintFileBreakdownChart({ data, isLoading }: PrintFileBreakdownChartProps) {
  const { t } = useTranslation('production');
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('qa.printFileIssuesByReason')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('qa.printFileIssuesByReason')}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={FileWarning}
            title={t('qa.noPrintFileIssuesTitle')}
            description={t('qa.noPrintFileIssuesDescription')}
          />
        </CardContent>
      </Card>
    );
  }

  let customColorIndex = 0;

  const items = data.map((item) => {
    const label = REASON_LABELS[item.reason] ||
      item.reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const isCustom = !(item.reason in KNOWN_REASON_COLORS);
    const color = getReasonColor(item.reason, isCustom ? customColorIndex++ : 0);
    return { ...item, label, color };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('qa.printFileIssuesByReason')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.reason}
              className="flex items-center gap-3 py-1.5"
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm flex-1 truncate">{item.label}</span>
              <span className="text-sm font-medium tabular-nums">{item.count}</span>
              <Badge variant="secondary" className="text-xs tabular-nums min-w-[48px] justify-center">
                {item.percentage}%
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
